import { ReactElement, useCallback, useMemo } from 'react';
import { Control, FieldValues, useForm } from 'react-hook-form';
import { Typography } from '@mui/material';
import BigNumber from 'bignumber.js';
import { Address } from 'viem';

import { useGetMainTokenBalanceQuery } from 'modules/api/actions/getMainTokenBalance';
import { useGetUnstakePeriodQuery } from 'modules/api/actions/getUnstakePeriod';
import { useApprove } from 'modules/api/hooks/useApprove';
import { mapDataToUndefinedIfSkip } from 'modules/api/utils';
import { GuardButton } from 'modules/auth/components/GuardButton';
import { useConnection } from 'modules/auth/hooks/useConnection';
import { Info } from 'modules/common/components/Info/Info';
import { NumberTextField } from 'modules/common/components/NumberTextField';
import { Summary } from 'modules/common/components/Summary';
import { SummaryItem } from 'modules/common/components/SummaryItem';
import {
  BUY_MAIN_MORE_LINK,
  DECIMAL_PLACES_SHORT,
  DEFAULT_DECIMAL_PLACES,
  HUNDRED,
  MAX_DECIMAL_PLACES,
  ZERO,
} from 'modules/common/const';
import { useGetUSDAmount } from 'modules/common/hooks/useGetUSDAmount';
import { useGlobalTranslation } from 'modules/i18n/hooks/useGlobalTranslation';
import { useGetAccountPoolQuery } from 'modules/pool/actions/getAccountPool';
import { useGetPoolQuery } from 'modules/pool/actions/getPool';
import { useGetPoolMetaQuery } from 'modules/pool/actions/getPoolMeta';
import { PoolInfoColumn } from 'modules/pool/components/PoolInfoColumn/PoolInfoColumn';
import { useGetPoolAPYs } from 'modules/pool/hooks/useGetPoolAPYs';

import { translation } from './translation';
import { useStyles } from './useStyles';

interface IDelegateContentProps {
  title?: string;
  poolAddress: Address;
  onSubmit: (params: { amount: BigNumber }) => void;
  isSubmitLoading: boolean;
}

interface IFormValues extends FieldValues {
  amount: string;
}

export function DelegateForm({
  title,
  poolAddress,
  onSubmit,
  isSubmitLoading,
}: IDelegateContentProps): ReactElement | null {
  const { t, keys } = useGlobalTranslation(translation);
  const { classes } = useStyles();
  const { isConnected, connectionKey, cacheTime } = useConnection();

  const { data: pool } = useGetPoolQuery(
    { address: poolAddress },
    { refetchOnMountOrArgChange: cacheTime },
  );
  const { data: poolMeta } = useGetPoolMetaQuery({ address: poolAddress });

  const { data: accountPool } = useGetAccountPoolQuery(
    { address: poolAddress, connectionKey },
    {
      skip: !isConnected,
      selectFromResult: mapDataToUndefinedIfSkip,
      refetchOnMountOrArgChange: cacheTime,
    },
  );
  const stakedAmount = accountPool?.stakedAmount ?? ZERO;

  const { data: balance = ZERO } = useGetMainTokenBalanceQuery(
    { connectionKey },
    {
      skip: !isConnected,
      selectFromResult: mapDataToUndefinedIfSkip,
      refetchOnMountOrArgChange: cacheTime,
    },
  );
  const { poolAPYs } = useGetPoolAPYs();
  const poolAPY = poolAPYs[poolAddress] ?? ZERO;

  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
    setValue,
  } = useForm<IFormValues>({
    mode: 'onChange',
    defaultValues: {
      amount: '',
    },
  });

  const amountInputValue = watch('amount');

  const convertedAmount = useMemo(() => {
    const convertedValue = new BigNumber(amountInputValue);

    return convertedValue.isNaN() ? ZERO : convertedValue;
  }, [amountInputValue]);

  const convertedAmountWithStaked = useMemo(
    () => convertedAmount.plus(stakedAmount),
    [convertedAmount, stakedAmount],
  );
  const convertedAmountWithStakedUSD = useGetUSDAmount(
    convertedAmountWithStaked,
  );

  const { data: unstakePeriodDays } = useGetUnstakePeriodQuery(
    { poolAddress },
    { refetchOnMountOrArgChange: cacheTime },
  );

  const {
    isApproved,
    isDisabled: isApproveDisabled,
    isLoading: isApproveLoading,
    onApprove,
  } = useApprove({ amount: convertedAmount, poolAddress });

  const onMaxClick = useCallback(
    () =>
      setValue('amount', balance.decimalPlaces(MAX_DECIMAL_PLACES).toString(), {
        shouldValidate: true,
      }),
    [balance, setValue],
  );

  const incomeAmount = useMemo(
    () => convertedAmount.multipliedBy(poolAPY.div(HUNDRED)),
    [convertedAmount, poolAPY],
  );
  const incomeAmountUSD = useGetUSDAmount(incomeAmount);

  if (!pool) {
    return null;
  }

  const isSubmitDisabled = !!errors.amount?.message || !watch('amount');
  const { commission } = pool;

  return (
    <form
      className={classes.root}
      onSubmit={handleSubmit(({ amount }) =>
        onSubmit({ amount: new BigNumber(amount) }),
      )}
    >
      <Typography
        mb={1}
        textAlign="center"
        textTransform="uppercase"
        variant="h2"
      >
        {title || t(keys.delegate)}
      </Typography>

      <PoolInfoColumn
        address={poolAddress}
        imageIpfsLink={poolMeta?.ipfsImageLink}
        name={poolMeta?.name}
      />

      <NumberTextField
        balance={balance}
        className={classes.input}
        control={control as unknown as Control<FieldValues>}
        error={!!errors.amount?.message}
        getMoreLink={BUY_MAIN_MORE_LINK}
        label={t(keys.inputTokenLabel, { token: t(keys.tokens.main) })}
        name="amount"
        placeholder="0"
        unit={t(keys.tokens.main)}
        variant="filled"
        onMaxClick={onMaxClick}
      />

      <div className={classes.content}>
        <div className={classes.infos}>
          {!incomeAmount.isZero() && (
            <Info>
              {t(
                keys.income,
                {
                  usdValue:
                    incomeAmountUSD?.decimalPlaces(DECIMAL_PLACES_SHORT),
                  value: incomeAmount.decimalPlaces(DEFAULT_DECIMAL_PLACES),
                  apy: poolAPY.decimalPlaces(2),
                  fee: commission,
                  token: t(keys.tokens.main),
                },
                true,
              )}
            </Info>
          )}

          {unstakePeriodDays && (
            <Info>{t(keys.period, { value: unstakePeriodDays })}</Info>
          )}
        </div>
      </div>

      <Summary className={classes.summary}>
        <SummaryItem
          label={t(keys.totalAmountLabel)}
          usdValue={convertedAmountWithStakedUSD}
          value={t(keys.unit.tokenValue, {
            value: convertedAmountWithStaked.decimalPlaces(8),
            token: t(keys.tokens.main),
          })}
        />
      </Summary>

      {isApproved ? (
        <GuardButton
          disabled={isApproveDisabled}
          loading={isSubmitLoading}
          size="large"
          sx={theme => ({ marginTop: theme.spacing(6) })}
          type="submit"
        >
          {t(keys.delegate)}
        </GuardButton>
      ) : (
        <GuardButton
          disabled={isSubmitDisabled}
          loading={isApproveLoading}
          size="large"
          sx={theme => ({ marginTop: theme.spacing(6) })}
          onClick={() => onApprove()}
        >
          {t(keys.approve)}
        </GuardButton>
      )}
    </form>
  );
}
